/**
 * Activity: what the agents did, today, this week or this month, across every project the person may
 * see (`GET /activity/v1`, packages/shared/src/schemas/activity.ts). The numbers for the window, the
 * agents running right now (only while some are), then a timeline grouped by day: each run one plain
 * sentence that opens the run's history, and between the runs the quieter things that were not runs
 * (an approval asked or answered, an agent's error, a team started, a task done). At the foot, folded
 * away, the plain events the machines reported; audit records are the Audit page's.
 */

import {
  activityDayLabel,
  activityTime,
  activityWindowStart,
  foldedSentence,
  foldMachineEvents,
  liveStatusText,
  LIVE_WORKING,
  type ActivityChoices,
  type ActivityResponse,
  type LiveAgent,
} from '@aioe/shared';
import { Clock, CircleExclamation, CreditCard, Display, Pulse, Wrench } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { routes } from '../api/client.ts';
import { liveLink, Timeline } from '../components/activityTimeline.tsx';
import { CloudRuns } from '../components/cloudRuns.tsx';
import { Button, ChoiceSelect, Empty, ErrorNote, ListBlock, ListGroup, ListRow, PageColumn, PageHeader, Stat, StatusMark } from '../components/ui.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { RowMenu } from '../components/ui/RowMenu.tsx';
import { VIEW_TONES, type ArtTone } from '../components/ui/Art.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';
import { useLoad, useNow } from '../hooks.ts';
import { holdsAnywhere, usePermissions } from '../permissions.ts';
import { HeldActions } from '../components/rolesPermissions.tsx';
import { useMoney } from '../money.tsx';

type WindowKey = 'today' | 'week' | 'month';
const WINDOWS: Record<WindowKey, { label: string; days: number; spoken: string }> = {
  today: { label: 'Today', days: 1, spoken: 'today' },
  week: { label: '7 days', days: 7, spoken: 'in the last 7 days' },
  month: { label: '30 days', days: 30, spoken: 'in the last 30 days' },
};
const ANYONE = 'all';


export function ActivityPage() {
  const tone = VIEW_TONES.activity!;
  const navigate = useNavigate();
  const now = useNow();
  const { format } = useMoney();
  const [windowKey, setWindowKey] = useState<WindowKey>('today');
  const [projectId, setProjectId] = useState<string>(ANYONE);
  const [who, setWho] = useState<string>(ANYONE);
  const [attention, setAttention] = useState(false);
  // The window starts at local midnight: the same value all day, so polls keep their ETag.
  const from = activityWindowStart(WINDOWS[windowKey].days, now);
  const query = useMemo(
    () => ({
      from,
      ...(projectId !== ANYONE ? { projectId } : {}),
      ...(who.startsWith('person:') ? { person: who.slice(7) } : {}),
      ...(who.startsWith('device:') ? { deviceId: who.slice(7) } : {}),
      ...(attention ? { attention: true } : {}),
    }),
    [from, projectId, who, attention],
  );
  const activity = useLoad(() => routes.activity(query), [query], 30_000);
  const live = useLoad(() => routes.live(), [], 15_000);
  const a = activity.data;
  // Older pages, asked for with "Show older"; a new query starts again.
  const [older, setOlder] = useState<{ key: string; pages: ActivityResponse[]; loading?: boolean; error?: string }>({ key: '', pages: [] });
  const queryKey = JSON.stringify(query);
  const pages = useMemo(() => (older.key === queryKey ? older.pages : []), [older, queryKey]);
  const next = (pages.at(-1) ?? a)?.next;
  const items = useMemo(() => [...(a?.items ?? []), ...pages.flatMap((p) => p.items)], [a, pages]);
  // The choices come with the first page; kept while a filter's answer loads.
  const [choices, setChoices] = useState<ActivityChoices>();
  if (a?.choices && a.choices !== choices) setChoices(a.choices);

  const showOlder = async () => {
    if (!next) return;
    setOlder({ key: queryKey, pages, loading: true });
    try {
      const page = await routes.activity({ ...query, before: next });
      setOlder({ key: queryKey, pages: [...pages, page] });
    } catch (e) {
      setOlder({ key: queryKey, pages, error: (e as Error).message });
    }
  };

  const agents = live.data?.agents ?? [];
  const devices = useMemo(() => live.data?.devices ?? [], [live.data]);
  const answering = devices.filter((d) => d.answered).length;
  // Agents waiting on a machine that answered, or approvals and questions still open on the platform: whichever is more.
  const waiting = live.data || a ? Math.max(live.data?.waiting ?? 0, a?.now.waiting ?? 0) : undefined;
  const t = a?.totals;
  const needsAttention = t && a ? t.failed + a.now.waiting : undefined;
  const filtered = projectId !== ANYONE || who !== ANYONE || attention;
  const projectOptions = useMemo(() => ({ [ANYONE]: 'Every project', ...Object.fromEntries((choices?.projects ?? []).map((p) => [p.id, p.name])) }), [choices]);
  const whoOptions = useMemo(
    () => ({
      [ANYONE]: 'Anyone, any machine',
      ...Object.fromEntries((choices?.people ?? []).map((p) => [`person:${p.id}`, p.name])),
      ...Object.fromEntries((choices?.machines ?? []).map((m) => [`device:${m.deviceId}`, `On ${m.name}`])),
    }),
    [choices],
  );
  const machineName = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of devices) m.set(d.deviceId, d.hostname);
    for (const d of choices?.machines ?? []) m.set(d.deviceId, d.name);
    return m;
  }, [devices, choices]);

  const resetFilters = () => {
    setProjectId(ANYONE);
    setWho(ANYONE);
    setAttention(false);
  };

  const perms = usePermissions().data;
  return (
    <PageColumn width="data">
      <PageHeader
        tone={tone}
        icon={<Pulse />}
        title="Activity"
        subtitle="What your agents did and are doing, across every project you can see."
        actions={
          <>
            <Segment aria-label="When" size="sm" selectedKey={windowKey} onSelectionChange={(k) => setWindowKey(String(k) as WindowKey)} className="w-fit">
              {(Object.keys(WINDOWS) as WindowKey[]).map((k) => (
                <Segment.Item key={k} id={k}>
                  {WINDOWS[k].label}
                </Segment.Item>
              ))}
            </Segment>
            <ChoiceSelect label="Project" value={projectId} options={projectOptions} onChange={setProjectId} className="w-44 max-w-full" />
            <ChoiceSelect label="Person or machine" value={who} options={whoOptions} onChange={setWho} className="w-48 max-w-full" />
          </>
        }
      />
      <Explainer id="activity" tone={tone} icon={<Pulse />} title="How Activity works" summary="Every run your agents made, day by day, and what waits on a person">
        <p>Each workbench and node uploads its runs as it works. A run is one task an agent took on: it shows here as one sentence, and opening it shows its history in the project.</p>
        <p>Between the runs are the quieter things that were not runs: an approval asked or answered, an agent’s error, a team started, a task done. Needs attention keeps only failed runs and what still waits.</p>
        <p>Agents running now can be stopped from here. What the machines reported in plain words is folded away at the foot; audit records are on the Audit page.</p>
      </Explainer>
      <PartBoundary key="activity" name="Activity" tone={tone}>
        <ErrorNote error={activity.error} />
        <div className="art-stats">
          <Stat tone={tone} icon={<Pulse />} label="Running now" value={answering > 0 ? agents.length : a?.now.running} detail={waiting ? `${waiting} waiting on a person` : 'Nothing waits on a person'} needsYou={(waiting ?? 0) > 0} />
          <Stat tone={tone} icon={<Clock />} label="Runs" value={t?.runs} detail={t ? `${t.finished.toLocaleString()} finished · ${t.failed.toLocaleString()} failed` : ''} />
          <Stat tone={tone} icon={<CircleExclamation />} label="Needs attention" value={needsAttention} detail={attention ? 'Showing only these · press to show all' : 'Failed runs and what waits'} onPress={() => setAttention((x) => !x)} selected={attention} />
          <Stat tone={tone} icon={<Wrench />} label="Tool calls" value={t?.toolCalls} detail={t ? `Across ${t.runs.toLocaleString()} ${t.runs === 1 ? 'run' : 'runs'} ${WINDOWS[windowKey].spoken}` : ''} />
          {t?.spendUsd !== undefined ? <Stat tone={tone} icon={<CreditCard />} label="Spend" value={format(t.spendUsd)} detail={a?.scope === 'organisation' ? `The organisation, ${WINDOWS[windowKey].spoken}` : `Your own use, ${WINDOWS[windowKey].spoken}`} /> : null}
          <Stat tone={tone} icon={<Display />} label="Machines" value={live.data ? `${answering} / ${devices.length}` : undefined} detail={live.data ? (devices.length ? `${answering} answering · see them all` : 'None enrolled yet') : 'Asking the fleet'} onPress={() => navigate('/workbenches')} />
        </div>

        {/* With roles on, whoever may decide an agent's held action (an administrator, a team's administrator) sees it here. */}
        {perms?.mode === 'roles' && holdsAnywhere(perms, 'agents.activity', 'act') ? <HeldActions quiet onDecided={activity.reload} /> : null}

        {agents.length > 0 ? (
          <ListGroup title="Running now" count={agents.length} description="Stop one or cancel its turn from its menu; open its name to follow the transcript.">
            {agents.map((ag) => (
              <AgentRow key={`${ag.deviceId}/${ag.sessionId}`} a={ag} onChanged={live.reload} />
            ))}
          </ListGroup>
        ) : null}

        <ListGroup
          title="Timeline"
          count={t?.runs}
          description={attention ? 'Failed runs, errors and what still waits.' : `Every run ${WINDOWS[windowKey].spoken}, newest first. Open one to see what it did.`}
          actions={
            filtered ? (
              <Button size="sm" variant="ghost" onPress={resetFilters}>
                Show everything
              </Button>
            ) : undefined
          }
          empty={
            a && items.length === 0 ? (
              <Empty
                tone={tone}
                icon={<Clock />}
                title={attention ? 'Nothing needs attention' : 'Nothing happened in this window'}
                body={filtered ? 'Nothing matches these filters. Widen the window or show everyone.' : 'Runs appear here as the workbenches and nodes upload them, with approvals, errors and finished tasks between them.'}
                action={
                  filtered ? (
                    <Button size="sm" variant="secondary" onPress={resetFilters}>
                      Show everything
                    </Button>
                  ) : windowKey !== 'month' ? (
                    <Button size="sm" variant="secondary" onPress={() => setWindowKey(windowKey === 'today' ? 'week' : 'month')}>
                      Look further back
                    </Button>
                  ) : undefined
                }
              />
            ) : undefined
          }
        >
          {!a && activity.loading ? <ListBlock className="text-muted">Reading what happened…</ListBlock> : null}
          {items.length ? (
            <ListBlock flush>
              <Timeline items={items} now={now} showProject={projectId === ANYONE} />
            </ListBlock>
          ) : null}
          {next || older.error ? (
            <ListBlock className="flex flex-wrap items-center gap-3">
              {next ? (
                <Button size="sm" variant="ghost" isPending={older.loading} onPress={() => void showOlder()}>
                  Show older
                </Button>
              ) : null}
              <ErrorNote error={older.error} />
            </ListBlock>
          ) : null}
        </ListGroup>

        <CloudRuns tone={tone} showProject hideWhenEmpty />
        <MachineEvents tone={tone} machineName={machineName} now={now} />
      </PartBoundary>
    </PageColumn>
  );
}

/** The plain events the machines reported, folded away until asked for. */
function MachineEvents({ tone, machineName, now }: { tone: ArtTone; machineName: Map<string, string>; now: number }) {
  const [open, setOpen] = useState(false);
  return (
    <ListGroup
      title="Events from machines"
      description="What the workbenches and nodes reported, in plain words. Audit records are on the Audit page."
      actions={
        <Button size="sm" variant="ghost" onPress={() => setOpen((o) => !o)} aria-expanded={open}>
          {open ? 'Hide' : 'Show'}
        </Button>
      }
    >
      {open ? <MachineEventList tone={tone} machineName={machineName} now={now} /> : <ListBlock className="text-muted">Folded away. Show them to read what each machine said.</ListBlock>}
    </ListGroup>
  );
}

function MachineEventList({ tone, machineName, now }: { tone: ArtTone; machineName: Map<string, string>; now: number }) {
  const events = useLoad(() => routes.events(undefined, 200, ['audit']), [], 30_000);
  const folded = useMemo(() => foldMachineEvents(events.data ?? []), [events.data]);
  if (!events.data && events.loading) return <ListBlock className="text-muted">Reading what the machines reported…</ListBlock>;
  if (events.error)
    return (
      <ListBlock>
        <ErrorNote error={events.error} />
      </ListBlock>
    );
  if (!folded.length)
    return (
      <ListBlock flush>
        <Empty tone={tone} icon={<Display />} title="Quiet so far" body="Machines report agent, team and task events as they work; they show here in plain words." compact />
      </ListBlock>
    );
  return (
    <ListBlock flush>
      <ol className="divide-y divide-separator/60">
        {folded.map((f) => {
          const day = activityDayLabel(new Date(f.at).toISOString(), now);
          return (
            <li key={f.key} className="flex items-baseline gap-4 px-4 py-2 text-sm">
              <div className="min-w-0 flex-1">
                {foldedSentence(f)}
                <span className="text-xs text-muted"> · {f.deviceId ? `on ${machineName.get(f.deviceId) ?? 'a machine'}` : 'a machine'}</span>
              </div>
              <span className="shrink-0 text-xs text-muted tabular-nums">{day === 'Today' ? activityTime(f.at) : `${day}, ${activityTime(f.at)}`}</span>
            </li>
          );
        })}
      </ol>
    </ListBlock>
  );
}

/** One agent running now: what it is, where, what it is doing, its state, and a menu with its two actions. */
function AgentRow({ a, onChanged }: { a: LiveAgent; onChanged: () => void }) {
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const navigate = useNavigate();
  const st = liveStatusText(a.status);
  const working = LIVE_WORKING.includes(a.status) || a.status === 'waiting_approval';
  const act = async (label: string, method: string) => {
    setBusy(label);
    setError(undefined);
    try {
      const r = (await routes.rpc(a.deviceId, method, { sessionId: a.sessionId })) as { error?: { message: string } };
      if (r?.error) throw new Error(r.error.message);
      setTimeout(onChanged, 800);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(undefined);
    }
  };
  const task = a.tasks.find((t) => t.status === 'in-progress') ?? a.tasks[0];
  const doing = a.currentTool ? `Using ${a.currentTool}` : task ? task.title : a.title;
  const where = [a.teamId === 'solo' ? undefined : `In ${a.teamId}`, a.projectName, a.hostname].filter(Boolean).join(' · ');
  const label = busy === 'cancel' ? 'Cancelling the turn' : busy === 'stop' ? 'Stopping' : a.approvals > 0 && a.status !== 'waiting_approval' ? `${st.text}, ${a.approvals} to approve` : st.text;
  return (
    <ListRow
      icon={<Pulse />}
      title={
        <Link to={liveLink(a.deviceId, a.sessionId)} className="hover:underline" title="Open the transcript">
          {a.name}
        </Link>
      }
      description={
        <>
          <span className="block truncate">{doing || where}</span>
          {doing && where ? <span className="block truncate">{where}</span> : null}
          {error ? <span className="block text-danger">{error}</span> : null}
        </>
      }
      status={<StatusMark state={st.mark} label={label} />}
      compactControl
      control={
        <>
          <RowMenu
            label={`More for ${a.name}`}
            items={[{ id: 'open', label: 'Open the transcript' }, working && { id: 'cancel', label: 'Cancel this turn', disabled: Boolean(busy) }, { id: 'stop', label: 'Stop the agent', danger: true, disabled: Boolean(busy) }]}
            onAction={(id) => (id === 'open' ? navigate(liveLink(a.deviceId, a.sessionId)) : void act(id, id === 'cancel' ? 'sessions.cancel' : 'sessions.stop'))}
          />
        </>
      }
    />
  );
}
